import {
  useCachedBusinessAddress,
  useCachedBusinessInsights,
  useCachedBusinessName,
  useCachedGeographicFocus,
  useCachedLLmsRule,
  useCachedRobotsTxtRule,
  useCachedSchemaRule,
  useCachedSelectedProject,
  useCachedTargetAudience,
  useCachedVisibilityScore,
} from 'hooks';
import { useCachedConversationalWritingRule } from 'hooks/api/cached/useCachedConversationalWritingRule';
import { useCachedContentFreshnessRule } from 'hooks/api/cached/useCachedContentFreshnessRule';
import { useCachedStatisticalEvidenceRule } from 'hooks/api/cached/useCachedStatisticalEvidenceRule';
import { useRuleStats } from 'shared/hooks/useRulesStat';
import { useAppSelector } from 'store/hooks';
import { formatDate } from 'shared/helpers/format-date';
import { ICONS } from 'shared/constants/images';
import {
  getScoreDisplayStatus,
  ScoreDisplayStatus,
} from 'shared/helpers/ruleScoring';
import { calculateTechnicalSectionStats } from 'shared/helpers/technicalOverviewStats';
import { TECHNICAL_ACCESSIBILITY_UNABLE_TO_CHECK_VALUE } from 'shared/helpers/technicalAccessibility';
import { translate } from 'services/translationService';
import {
  InsightsService,
  RobotsRuleResponse,
  RuleEntity,
  ScoreEnum,
  TechnicalRuleResponse,
} from 'store/api/apiTypes';
import { Report } from '../../export-modal.types';
import { applyStaticTranslations } from './report-preview.i18n';
import {
  BicAttrPreviewData,
  BicPreviewData,
  OfferingsPreviewData,
  PreviewData,
  ProductRulesPreviewData,
  TechnicalPreviewData,
  Translate,
} from './report-preview.types';

const STATUS_CLASS: Record<ScoreDisplayStatus, string> = {
  pass: 's-green',
  partial: 's-amber',
  fail: 's-red',
};

const STATUS_LABEL: Record<ScoreDisplayStatus, string> = {
  pass: 'fullyReady',
  partial: 'partiallyReady',
  fail: 'notReady',
};

const LLMS_KEYS = ['file', 'structure', 'products'] as const;
const SCHEMA_KEYS = [
  'schema',
  'organization_schema',
  'website_schema',
] as const;

const BIC_ATTRS = [
  {
    key: 'business_name' as const,
    subKeys: [
      'entity_disambiguation',
      'contextual_association',
      'basic_consistency',
    ],
  },
  {
    key: 'business_address' as const,
    subKeys: [
      'address_completeness_accuracy',
      'contact_integration',
      'consistency_presentation',
    ],
  },
  {
    key: 'target_audience' as const,
    subKeys: [
      'presentation_quality',
      'consistency_across_pages',
      'specificity',
      'llm_citation_potential',
    ],
  },
  {
    key: 'geographic_focus' as const,
    subKeys: [
      'service_delivery_evidence',
      'specificity_for_ai_citation',
      'evidence_consistency',
    ],
  },
];

const scoreStatus = (
  score: number | null | undefined,
  max: number | null | undefined = 10
): ScoreDisplayStatus => {
  if (score == null || !Number.isFinite(score)) return 'fail';
  const pct = score / (max && max > 0 ? max : 10);
  return pct >= 0.8 ? 'pass' : pct >= 0.4 ? 'partial' : 'fail';
};

type TechLevel = 'high' | 'medium' | 'low' | 'error';

const techLevel = (
  score: number | null | undefined,
  max: number | null | undefined = 10
): TechLevel => {
  if (score == null || !Number.isFinite(score)) return 'error';
  const pct = score / (max && max > 0 ? max : 10);
  return pct >= 0.8 ? 'high' : pct >= 0.4 ? 'medium' : 'low';
};

const formatScore = (n: number) => Number(n.toFixed(2)).toString();
const formatScore1 = (n: number) => Number(n.toFixed(1)).toString();

// Placeholder shown instead of a number for a rule the backend couldn't evaluate.
const NO_SCORE = '-';

type TechRuleName = 'robots' | 'llms' | 'schema';

// One entry per rule in the Technical Accessibility section.
//  - existsFinding: the finding whose `exists: "unable_to_check"` marks the
//    whole rule as unevaluated, matching getForcedErrorRuleKeys in the app's
//    technical-accessibility helpers.
//  - weight: share of the section score, renormalized when a rule drops out.
//  - items: the rule's check ids, which double as DOM id and i18n key stems.
const TECH_RULE_SPECS: ReadonlyArray<{
  name: TechRuleName;
  existsFinding: string;
  weight: number;
  items: readonly string[];
  statsKey: 'robotsRuleData' | 'llmsRuleData' | 'schemaRuleData';
}> = [
  {
    name: 'robots',
    existsFinding: 'file',
    weight: 0.45,
    items: ['robots-txt'],
    statsKey: 'robotsRuleData',
  },
  {
    name: 'llms',
    existsFinding: 'file',
    weight: 0.25,
    items: LLMS_KEYS,
    statsKey: 'llmsRuleData',
  },
  {
    name: 'schema',
    existsFinding: 'schema',
    weight: 0.3,
    items: SCHEMA_KEYS,
    statsKey: 'schemaRuleData',
  },
];

const TECH_RULE_EXISTS_FINDING: Record<TechRuleName, string> =
  Object.fromEntries(
    TECH_RULE_SPECS.map(spec => [spec.name, spec.existsFinding])
  ) as Record<TechRuleName, string>;

// A technical rule the backend couldn't evaluate: the score comes back null and
// the trigger finding reports exists: "unable_to_check" (e.g. the fetch was
// answered with 403). Sub-check scores can't be used to detect this — an
// unevaluated llms.txt still reports findings.file.score = 0 — so the decision
// is made at rule level, and then applies to every check under it.
const isTechRuleUnscored = (
  rule: RobotsRuleResponse | TechnicalRuleResponse | undefined,
  existsFinding: string
): boolean => {
  if (!rule) return false;
  const findings = rule.findings as
    | Record<string, { exists?: unknown } | undefined>
    | undefined;
  return (
    findings?.[existsFinding]?.exists ===
      TECHNICAL_ACCESSIBILITY_UNABLE_TO_CHECK_VALUE ||
    rule.score == null ||
    !Number.isFinite(rule.score)
  );
};

const TECH_TITLE_KEY: Record<string, string> = {
  products: 'products_section',
  schema: 'schema_exists',
};

// Maps API finding fields to `finding_<key>` translation keys, mirroring
// TECHNICAL_BOOLEAN_FINDINGS_KEYS in widgets/technical-accessibility/helpers.ts.
const TECH_FINDINGS_KEYS: Record<string, Record<string, string>> = {
  structure: {
    h1_present: 'h1_present',
    blockquote_present: 'blockquote_present',
    h2_present: 'h2_present',
    links_present: 'links_present',
  },
  products: {
    products_present: 'products_present',
    services_present: 'services_present',
  },
};

const TECH_FINDINGS_VIEW_POLICY: Record<
  string,
  Partial<Record<TechLevel, 'text' | 'matrix'>> & {
    default: 'text' | 'matrix';
  }
> = {
  'robots-txt': { default: 'text', medium: 'matrix' },
  file: { default: 'text' },
  structure: { default: 'text', medium: 'matrix', low: 'matrix' },
  products_section: { default: 'text' },
};

const getTechViewMode = (
  titleKey: string,
  level: TechLevel
): 'text' | 'matrix' => {
  const policy = TECH_FINDINGS_VIEW_POLICY[titleKey];
  if (policy) return policy[level] ?? policy.default;
  return level === 'high' ? 'text' : 'matrix';
};

const isBotAllowed = (
  bot: { allowed?: boolean; blocks_paths?: string[] } | undefined
) => Boolean(bot?.allowed) && !(bot?.blocks_paths?.length ?? 0);

// Extract a { label: boolean } map from raw API findings, mirroring the shape
// the app builds in robosRuleToInfoCompletenessScore / normalizeTechnicalFindings.
const extractBooleanMap = (
  key: string,
  findings: Record<string, unknown> | undefined
): Record<string, boolean> | null => {
  if (!findings) return null;
  if (key === 'robots-txt') {
    return {
      gpt_bot: isBotAllowed(findings.gpt_bot as any),
      claude_bot: isBotAllowed(findings.claude_bot as any),
      google_extended: isBotAllowed(findings.google_extended as any),
      perplexity_bot: isBotAllowed(findings.perplexity_bot as any),
      chat_gpt_user: isBotAllowed(findings.chat_gpt_user as any),
    };
  }
  const keysMap = TECH_FINDINGS_KEYS[key];
  if (keysMap) {
    const out: Record<string, boolean> = {};
    for (const [field, translationKey] of Object.entries(keysMap)) {
      const v = findings[field];
      if (typeof v === 'boolean') out[translationKey] = v;
    }
    return Object.keys(out).length > 0 ? out : null;
  }
  const {
    score: _s,
    max_score: _m,
    ...rest
  } = findings as Record<string, unknown>;
  const values = Object.values(rest);
  if (values.length > 0 && values.every(v => typeof v === 'boolean')) {
    return rest as Record<string, boolean>;
  }
  return null;
};

const escapeHtml = (s: string) =>
  s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

// snake_case → "Title Case", used as the fallback for finding labels the
// backend adds before they get a translation.
const humanizeFindingKey = (key: string): string =>
  key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

// Findings keys arrive from the API as snake_case and are localized via the
// `finding_<key>` convention shared with shared/ui/rule-drawer/renderFindings.
const translateFindingKey = (key: string): string =>
  translate(`finding_${key}`, '_global', humanizeFindingKey(key));

const noneLabel = (): string => translate('findingsNone', '_global', 'None');

// A finding value that reads like a URL / path / domain (for link styling).
const isUrlLike = (s: string): boolean =>
  /^https?:\/\//i.test(s) || /^\//.test(s) || /^[\w-]+(\.[\w-]+)+/.test(s);

// One finding value rendered as HTML — URL-like values become link-styled text
// (.ins-link, primary colour); empty (null / '' / []) becomes "None".
const formatFindingValueHtml = (value: unknown): string => {
  const renderOne = (item: unknown): string => {
    // Nested object → "Sub Label: value" pairs (avoids "[object Object]").
    if (item && typeof item === 'object') {
      return Object.entries(item as Record<string, unknown>)
        .map(
          ([k, v]) =>
            `${escapeHtml(translateFindingKey(k))}: ${formatFindingValueHtml(v)}`
        )
        .join(' · ');
    }
    const s = String(item).trim();
    return isUrlLike(s)
      ? `<span class="ins-link">${escapeHtml(urlLabel(s))}</span>`
      : escapeHtml(String(item));
  };
  if (value == null) return noneLabel();
  if (Array.isArray(value))
    return value.length ? value.map(renderOne).join(' · ') : noneLabel();
  if (typeof value === 'object') return renderOne(value);
  if (typeof value === 'string')
    return value.trim() ? renderOne(value) : noneLabel();
  return String(value);
};

// Build the "– Label: value" rows for a BIC sub-rule's findings object.
// Data present but empty → a single "None" row (per product requirement).
const renderBicFindings = (findings: unknown): string => {
  if (!findings || typeof findings !== 'object') {
    return `<div class="cfd-row">${escapeHtml(noneLabel())}</div>`;
  }
  const row = (label: string, v: unknown) =>
    `<div class="cfd-row">– ${escapeHtml(translateFindingKey(label))}: ${formatFindingValueHtml(v)}</div>`;
  const entries = Object.entries(findings as Record<string, unknown>);
  if (entries.length === 0)
    return `<div class="cfd-row">${escapeHtml(noneLabel())}</div>`;
  return entries
    .map(([k, v]) => {
      // A nested object (e.g. components_found) is flattened: one row per
      // sub-entry, dropping the parent label — matches the design.
      if (v && typeof v === 'object' && !Array.isArray(v)) {
        return Object.entries(v as Record<string, unknown>)
          .map(([sk, sv]) => row(sk, sv))
          .join('');
      }
      return row(k, v);
    })
    .join('');
};

// A page URL's short label: hostname for the homepage ("/"), else the path.
const urlLabel = (u: string): string => {
  try {
    const parsed = new URL(u);
    const path = parsed.pathname.replace(/\/+$/, '');
    return path || parsed.hostname.replace(/^www\./, '');
  } catch {
    return u;
  }
};

// A text element that only *looks* like a link (the report is print/PDF — no
// clickable anchors). The .ins-link class carries the link styling.
const renderUrlLink = (u: string): string =>
  `<span class="ins-link">${escapeHtml(urlLabel(u))}</span>`;

const renderStatementItems = (
  statements: NonNullable<
    BicAttrPreviewData['extraction']
  >['explicit_statements']
) =>
  (statements ?? [])
    .map(
      s =>
        `<li class="ins-item">
          <div class="ins-context">"${escapeHtml(s.context)}"</div>
          <div class="ins-attr">${escapeHtml(s.quote)}${s.quote && s.page_url ? ' · ' : ''}${s.page_url ? renderUrlLink(s.page_url) : ''}</div>
        </li>`
    )
    .join('');

const renderIndicatorItems = (
  indicators: NonNullable<
    BicAttrPreviewData['extraction']
  >['implied_indicators']
) =>
  (indicators ?? [])
    .map(
      i =>
        `<li class="ins-item">
          <div class="ins-fact">${escapeHtml(i.signal)}</div>
          <div class="ins-attr">${escapeHtml(i.reasoning)}${i.page_url ? ` · ${renderUrlLink(i.page_url)}` : ''}</div>
        </li>`
    )
    .join('');

// Analysis Context (page_urls_analyzed) as link-styled text elements.
const renderInsLinks = (urls: string[] | undefined): string =>
  !urls || urls.length === 0 ? '' : urls.map(renderUrlLink).join(' · ');

const renderInsightsHtml = (
  extraction: BicAttrPreviewData['extraction'],
  t: Translate
): string => {
  const statements = extraction?.explicit_statements ?? [];
  const indicators = extraction?.implied_indicators ?? [];
  const parts: string[] = [];
  if (statements.length > 0) {
    parts.push(
      `<div class="ins-label">${escapeHtml(t('explicitStatements'))}</div><ul class="ins-list">${renderStatementItems(statements)}</ul>`
    );
  }
  if (indicators.length > 0) {
    parts.push(
      `<div class="ins-label">${escapeHtml(t('impliedIndicators'))}</div><ul class="ins-list">${renderIndicatorItems(indicators)}</ul>`
    );
  }
  return parts.join('');
};

const renderOfferingHtml = (
  productName: string,
  product: OfferingsPreviewData['products'][string],
  service: InsightsService | undefined,
  _ownDomain: string | undefined,
  t: Translate,
  index: number,
  total: number
) => {
  const entity = product.entity;
  const competitors = product.competitors ?? [];
  // AI Visibility score comes from the product entity's mention_rate (0–10).
  const scoreCls = STATUS_CLASS[scoreStatus(entity.mention_rate)];
  const identified = service?.context?.product_identified;

  const totalQueries = entity.query_metrics?.length ?? 0;
  const successCount = (entity.query_metrics ?? []).filter(
    q => q.total_mentions > 0
  ).length;
  const failCount = totalQueries - successCount;
  const successCls =
    STATUS_CLASS[scoreStatus(successCount, totalQueries || 10)];
  const avgRankCls =
    STATUS_CLASS[
      scoreStatus(
        Number.isFinite(entity.mean_rank) && entity.mean_rank > 0
          ? Math.max(0, 20 - entity.mean_rank)
          : 0,
        20
      )
    ];

  const queryRowsHtml = (entity.query_metrics ?? [])
    .map((qm, i) => {
      const mentioned = qm.total_mentions > 0;
      const avgText = mentioned ? `${Number(qm.mean_rank.toFixed(0))}/20` : '—';
      const bestRanks = (qm.execution_details ?? [])
        .filter(d => d.mentioned && d.rank != null)
        .map(d => d.rank as number);
      const bestText = bestRanks.length ? `${Math.min(...bestRanks)}/20` : '—';
      const statusText = mentioned
        ? t('success') || 'Success'
        : t('fail') || 'Fail';
      const statusCls = mentioned ? 's-green' : 's-red';
      return `<tr><td class="q"><span class="q-num">${i + 1}</span>${escapeHtml(qm.query)}</td>
        <td>${qm.total_mentions}/${qm.total_executions}</td>
        <td>${avgText}</td>
        <td>${bestText}</td>
        <td class="st ${statusCls}">${escapeHtml(statusText)}</td></tr>`;
    })
    .join('');

  const competitorRowsHtml = (() => {
    type Row = {
      name: string;
      domain: string;
      mentions: number;
      avg: number;
      score: number;
      isYou: boolean;
    };
    const toRow = (
      name: string,
      domain: string,
      metrics: typeof entity.query_metrics,
      meanRank: number,
      mentionRate: number,
      isYou: boolean
    ): Row => {
      const mentions = (metrics ?? []).filter(q => q.total_mentions > 0).length;
      const rankAvg = Number.isFinite(meanRank) && meanRank > 0 ? meanRank : 0;
      // AI Visibility = the entity's mention_rate (same metric as the offering).
      const score = Number.isFinite(mentionRate) ? mentionRate : 0;
      return { name, domain, mentions, avg: rankAvg, score, isYou };
    };
    const rows: Row[] = [
      toRow(
        entity.name,
        entity.domain,
        entity.query_metrics,
        entity.mean_rank,
        entity.mention_rate,
        true
      ),
      ...competitors.map(c =>
        toRow(
          c.name,
          c.domain,
          c.query_metrics,
          c.mean_rank,
          c.mention_rate,
          false
        )
      ),
    ];
    return rows
      .map(r => {
        const rowCls = STATUS_CLASS[scoreStatus(r.score)];
        const barClass =
          rowCls === 's-green'
            ? 'bg-green'
            : rowCls === 's-amber'
              ? 'bg-amber'
              : 'bg-red';
        const width = Math.max(4, Math.min(100, Math.round(r.score * 10)));
        return `<div class="cmp-row${r.isYou ? ' you' : ''}">
          <div class="cm-name"><div class="cm-n">${escapeHtml(r.name)}</div><span class="cm-a">${escapeHtml(r.domain)}</span></div>
          <div class="cm-qs">${r.mentions}/${totalQueries || 10}</div>
          <div class="cm-avg">${r.avg > 0 ? r.avg.toFixed(2) : '—'}/20</div>
          <div class="cmp-bar"><i class="${barClass}" style="width:${width}%"></i></div>
          <div class="cm-sc ${rowCls}">${r.score}</div>
        </div>`;
      })
      .join('');
  })();

  const confidence = identified?.confidence_level ?? '';
  const evidence = identified?.evidence_strength ?? '';
  // LLM Identification Insights come from the business-insights service's
  // product_extraction (explicit statements + implied indicators).
  const insightsContent = renderInsightsHtml(
    service?.context?.product_extraction,
    t
  );

  // LLM Identification Insights block — omitted entirely without explicit
  // statements or implied indicators, matching the BIC attributes: the
  // confidence/evidence meta on its own has nothing to evidence.
  const insxHtml = insightsContent
    ? `<div class="check insx">
        <div class="insx-head">
          <div class="c-title">${escapeHtml(t('llmIdentificationInsights') || 'LLM Identification Insights')}</div>
          <div class="insx-meta">${escapeHtml(t('confidence') || 'Confidence')}: <b>${escapeHtml(confidence)}</b> · ${escapeHtml(t('evidence') || 'Evidence')}: <b>${escapeHtml(evidence)}</b></div>
        </div>
        <div class="insx-desc">${escapeHtml(t('llmIdentificationInsightsDesc') || 'Here is how AI worked this out: which pages it read, what your site says outright, and what merely hints at it. Confidence is how sure AI is; Evidence is how solid the proof it found.')}</div>
        ${insightsContent}
      </div>`
    : '';

  const subHead = [
    escapeHtml(service?.geographic_focus ?? ''),
    escapeHtml(service?.target_audience ?? ''),
  ]
    .filter(Boolean)
    .join(' · ');

  const avgRankText =
    Number.isFinite(entity.mean_rank) && entity.mean_rank > 0
      ? `${entity.mean_rank.toFixed(2)} / 20`
      : '— / 20';

  // Origin chips ("Identified by AI" / "Added manually") disabled for now.
  // const isManual = !identified;
  // const originChip = isManual
  //   ? `<span class="chip-manual">${escapeHtml(t('addedManually') || 'Added manually')}</span>`
  //   : `<span class="chip-ai">${escapeHtml(t('identifiedByAi') || 'Identified by AI')}</span>`;
  const originChip = '';

  return `<div class="sgrid oh">
    <div class="srail"><div class="srail-label faint">${escapeHtml(t('offering') || 'Offering')} ${index} ${escapeHtml(t('of') || 'of')} ${total}</div></div>
    <div class="scol">
      <div class="oh-row">
        <div class="oh-stack">
          <div class="s2-title">${escapeHtml(productName)}</div>
          <div class="s2-desc">${subHead}</div>
        </div>
        <div class="oh-score">
          <div class="s2-score ${scoreCls}">${formatScore(entity.mention_rate)}</div>
          <div class="score-cap">${escapeHtml(t('visibility') || 'AI Visibility')}</div>
        </div>
      </div>
    </div>
  </div>
  <div class="sgrid">
    <div class="srail">${originChip}</div>
    <div class="scol checks">
      <div class="rh-row plain">
        <div class="rh-stack">
          <div class="rh-name">${escapeHtml(t('offeringVisibilityContext') || 'Offering Visibility Context')}</div>
          <div class="rh-desc">${escapeHtml(t('offeringVisibilityContextDesc') || "How AI understood this offering from your website — its name, what it does, who it's for, and where you sell it.")}</div>
        </div>
      </div>
      <div class="check">
        <div class="fields">
          <div class="f-row"><div class="f-key">${escapeHtml(t('name') || 'Name')}</div><div class="f-val">${escapeHtml(productName)}</div></div>
          <div class="f-row"><div class="f-key">${escapeHtml(t('description') || 'Description')}</div><div class="f-val">${escapeHtml(service?.description ?? entity.description ?? '')}</div></div>
          <div class="f-row"><div class="f-key">${escapeHtml(t('targetAudience') || 'Target Audience')}</div><div class="f-val">${escapeHtml(service?.target_audience ?? '')}</div></div>
          <div class="f-row"><div class="f-key">${escapeHtml(t('geographicFocus') || 'Geographic Focus')}</div><div class="f-val">${escapeHtml(service?.geographic_focus ?? '')}</div></div>
        </div>
      </div>
      ${insxHtml}
    </div>
  </div>
  <div class="sgrid">
    <div class="srail">
      <div class="s-tally">
        <div class="rt-kicker">${escapeHtml(t('queries') || 'Queries')}</div>
        <div class="rt-line"><i></i><span>${totalQueries} ${escapeHtml(t('total') || 'total')}</span></div>
        <div class="rt-line s-green"><i></i><span>${successCount} ${escapeHtml(t('mentionedYou') || 'mentioned you')}</span></div>
        <div class="rt-line s-red"><i></i><span>${failCount} ${escapeHtml(t('noMention') || 'no mention')}</span></div>
      </div>
    </div>
    <div class="scol">
      <div class="rh-row plain">
        <div class="rh-stack">
          <div class="rh-name">${escapeHtml(t('visibility') || 'AI Visibility')}</div>
          <div class="rh-desc">${escapeHtml(t('aiVisibilityDesc') || 'We generated several realistic search queries for this offering and tested each multiple times to track visibility in AI responses — measuring mention frequency and ranking position. Higher is better (10 = maximum visibility).')}</div>
        </div>
        <div class="rh-score-box">
          <div class="rh-score ${scoreCls}">${formatScore(entity.mention_rate)}</div>
          <div class="score-cap">${escapeHtml(t('score') || 'Score')}</div>
        </div>
      </div>
      <div class="mx">
        <div class="mx-row">
          <div class="mx-lab"><b>${escapeHtml(t('querySuccess') || 'Query Success')}</b> <span class="mx-note">${escapeHtml(t('querySuccessReport') || 'searches where AI mentioned you')}</span></div>
          <div class="mx-v ${successCls}">${successCount} ${escapeHtml(t('of') || 'of')} ${totalQueries}</div>
        </div>
        <div class="mx-row">
          <div class="mx-lab"><b>${escapeHtml(t('averagePosition') || 'Average Position')}</b> <span class="mx-note">${escapeHtml(t('averagePositionReport') || 'your place in the answer when mentioned — 1 is best')}</span></div>
          <div class="mx-v ${avgRankCls}">${avgRankText}</div>
        </div>
      </div>
      <div class="l3h">${escapeHtml(t('humanLikeSearchQueries') || 'Human-like Search Queries')}</div>
      <div class="l3d">${escapeHtml(t('humanLikeSearchQueriesDesc') || 'The searches we ran on AI — generated for this offering, or written by you. Mentioned shows how many times your brand came up.')}</div>
      <table class="qt">
        <thead><tr><th class="q">${escapeHtml(t('query') || 'Search')}</th><th>${escapeHtml(t('mentions') || 'Mentioned')}</th><th>${escapeHtml(t('avgPos') || 'Avg. position')}</th><th>${escapeHtml(t('bestPos') || 'Best position')}</th><th>${escapeHtml(t('status') || 'Result')}</th></tr></thead>
        <tbody>${queryRowsHtml}</tbody>
      </table>
      <div class="cmp-h">${escapeHtml(t('competitorComparison') || 'Competitor comparison')}</div>
      <div class="cmp-d">${escapeHtml(t('competitorComparisonDesc') || 'Who else AI mentioned in those same answers, and how you rank against them.')}</div>
      <div class="cmp-head"><div class="cm-name">${escapeHtml(t('company') || 'Company')}</div><div class="cm-qs">${escapeHtml(t('querySuccess') || 'Query Success')}</div><div class="cm-avg">${escapeHtml(t('avgPos') || 'Avg. pos')}</div><div class="cmp-bar-h">${escapeHtml(t('visibility') || 'AI Visibility')}</div></div>
      <div class="cmp-rows">${competitorRowsHtml}</div>
    </div>
  </div>`;
};

const renderOfferingsHtml = (
  offerings: OfferingsPreviewData,
  ownDomain: string | undefined,
  t: Translate
) => {
  const entries = Object.entries(offerings.products ?? {});
  if (entries.length === 0) return '';
  const total = entries.length;
  return entries
    .map(([name, product], i) => {
      // Offering identity (name/description/audience/geo/insights) comes from
      // the business-insights service. Product keys and service names don't
      // align in the data, so match by position — both lists are the same
      // offerings in order. The visibility product supplies the score/queries.
      const service = offerings.services[i];
      return renderOfferingHtml(
        service?.name ?? name,
        product,
        service,
        ownDomain,
        t,
        i + 1,
        total
      );
    })
    .join('');
};

// Table-of-contents offerings list: service name + AI Visibility (mention_rate),
// same offerings (service ↔ product matched by position) as the section body.
const renderTocOfferings = (offerings: OfferingsPreviewData): string => {
  const entries = Object.entries(offerings.products ?? {});
  if (entries.length === 0) return '';
  return entries
    .map(([name, product], i) => {
      const label = offerings.services[i]?.name ?? name;
      const score = product.entity.mention_rate;
      const cls = STATUS_CLASS[scoreStatus(score)];
      return `<div class="tsr"><div class="tsr-name">${escapeHtml(label)}</div><div class="tsr-score ${cls}">${formatScore(score)}</div></div>`;
    })
    .join('');
};

const TECH_ATTR_GROUPS: Array<{
  attrName: string;
  ruleData: 'robots' | 'llms' | 'schema';
  items: ReadonlyArray<{ key: string; titleKey: string }>;
}> = [
  {
    attrName: 'robots-txt',
    ruleData: 'robots',
    items: [{ key: 'robots-txt', titleKey: 'robots-txt' }],
  },
  {
    attrName: 'llms_txt_configuration',
    ruleData: 'llms',
    items: [
      { key: 'file', titleKey: 'file' },
      { key: 'structure', titleKey: 'structure' },
      { key: 'products', titleKey: 'products_section' },
    ],
  },
  {
    attrName: 'structured_data',
    ruleData: 'schema',
    items: [
      { key: 'schema', titleKey: 'schema_exists' },
      { key: 'organization_schema', titleKey: 'organization_schema' },
      { key: 'website_schema', titleKey: 'website_schema' },
    ],
  },
];

// Reuse the existing report*Desc translations (present in EN + DE) instead of
// per-key `${titleKey}_desc` / `${attrName}_desc` slugs, which don't exist and
// would render the raw key. Maps a rule's attrName or an item's titleKey.
const TECH_DESC_KEY: Record<string, string> = {
  llms_txt_configuration: 'reportLlmsRuleDesc',
  structured_data: 'reportSchemaRuleDesc',
  file: 'reportLlmsFileDesc',
  structure: 'reportLlmsStructureDesc',
  products_section: 'reportProductsSectionDesc',
  schema_exists: 'reportSchemaExistsDesc',
  organization_schema: 'reportOrganizationSchemaDesc',
  website_schema: 'reportWebsiteSchemaDesc',
};

const BIC_ATTR_TITLE_KEY: Record<string, string> = {
  business_name: 'company_name',
  business_address: 'business_address',
  target_audience: 'target_audience',
  geographic_focus: 'geographic_focus',
};

// Reuse the existing report*Desc translations (present in EN + DE) instead of
// non-existent `${key}_desc` slugs that would render the raw key. Covers both
// BIC attribute headers and their sub-rules.
const BIC_DESC_KEY: Record<string, string> = {
  // attribute headers (BIC_ATTR_TITLE_KEY values)
  company_name: 'reportCompanyNameDesc',
  business_address: 'reportBusinessAddressDesc',
  target_audience: 'reportTargetAudienceDesc',
  geographic_focus: 'reportGeographicFocusDesc',
  // sub-rules
  entity_disambiguation: 'reportEntityDisambiguationDesc',
  contextual_association: 'reportContextualAssociationDesc',
  basic_consistency: 'reportBasicConsistencyDesc',
  address_completeness_accuracy: 'reportAddressCompletenessDesc',
  contact_integration: 'reportContactIntegrationDesc',
  consistency_presentation: 'reportConsistencyPresentationDesc',
  presentation_quality: 'reportPresentationQualityDesc',
  consistency_across_pages: 'reportConsistencyAcrossPagesDesc',
  specificity: 'reportSpecificityDesc',
  llm_citation_potential: 'reportLlmCitationPotentialDesc',
  service_delivery_evidence: 'reportServiceDeliveryEvidenceDesc',
  specificity_for_ai_citation: 'reportSpecificityForAiCitationDesc',
  evidence_consistency: 'reportEvidenceConsistencyDesc',
};

const OFFERING_PRODUCT_RULES: Array<{
  key: keyof ProductRulesPreviewData;
  titleKey: string;
}> = [
  { key: 'conversational', titleKey: 'conversational_content_writing' },
  { key: 'statistical', titleKey: 'statistical_evidence_integration' },
  { key: 'freshness', titleKey: 'content_freshness_indicators' },
];

const renderFixItem = ({
  title,
  desc,
  missing,
  fix,
  t,
}: {
  title: string;
  desc?: string;
  missing?: string;
  fix?: string;
  t: Translate;
}) => {
  const missingLabel = t('reportFixMissing');
  const fixLabel = t('howToImprove');
  const missingHtml = missing
    ? `<div class="fx-line"><span class="fx-miss">${escapeHtml(missingLabel)}:</span> ${escapeHtml(missing)}</div>`
    : '';
  // A multi-line "how to improve" (e.g. LLMs.txt structure) renders as a
  // numbered list; a single line stays inline.
  const fixSteps = fix
    ? fix
        .split('\n')
        .map(s => s.trim())
        .filter(Boolean)
    : [];
  const fixHtml =
    fixSteps.length > 1
      ? `<div class="fx-line"><span class="fx-imp">${escapeHtml(fixLabel)}:</span></div><ol class="fx-ol">${fixSteps
          .map(s => `<li>${escapeHtml(s)}</li>`)
          .join('')}</ol>`
      : fix
        ? `<div class="fx-line"><span class="fx-imp">${escapeHtml(fixLabel)}:</span> ${escapeHtml(fix)}</div>`
        : '';
  const descHtml = desc ? `<div class="c-desc">${escapeHtml(desc)}</div>` : '';
  return `<div class="check">
    <div class="c-head">
      <div class="c-stack">
        <div class="c-title">${escapeHtml(title)}</div>
        ${descHtml}
      </div>
    </div>
    ${missingHtml}
    ${fixHtml}
  </div>`;
};

const renderSgridWithFixes = (
  attrTitle: string,
  attrDesc: string | undefined,
  fixItems: string[],
  t: Translate
) => {
  const fixLabel =
    fixItems.length === 1
      ? t('needFix') || 'need fix'
      : t('needFix') || 'need fix';
  const descHtml = attrDesc
    ? `<div class="rh-desc">${escapeHtml(attrDesc)}</div>`
    : '';
  return `<div class="sgrid">
    <div class="srail">
      <div class="s-tally">
        <div class="rt-kicker">${escapeHtml(t('checks') || 'Checks')}</div>
        <div class="rt-line s-red"><i></i><span>${fixItems.length} ${escapeHtml(fixLabel)}</span></div>
      </div>
    </div>
    <div class="scol checks">
      <div class="rh-row">
        <div class="rh-stack">
          <div class="rh-name">${escapeHtml(attrTitle)}</div>
          ${descHtml}
        </div>
      </div>
      ${fixItems.join('')}
    </div>
  </div>`;
};

const renderTechImprovementsHtml = (
  data: TechnicalPreviewData,
  t: Translate
) => {
  return TECH_ATTR_GROUPS.map(group => {
    const rule = data[group.ruleData];
    if (!rule) return '';
    // When a rule couldn't be evaluated, every check under it uses the "error"
    // copy — including the ones the backend omits from `findings` entirely, and
    // the trigger check itself, which still reports a misleading score of 0.
    const unscored = isTechRuleUnscored(
      rule,
      TECH_RULE_EXISTS_FINDING[group.ruleData]
    );
    const fixItems: string[] = [];
    for (const { key, titleKey } of group.items) {
      const finding =
        group.ruleData === 'robots'
          ? { score: rule.score, max_score: (rule as any).max_score }
          : (rule as any).findings?.[key];
      if (!finding && !unscored) continue;
      const level = unscored
        ? 'error'
        : techLevel(finding.score, finding.max_score);
      if (level === 'high') continue;
      const descKey = TECH_DESC_KEY[titleKey];
      fixItems.push(
        renderFixItem({
          title: t(titleKey),
          desc: descKey ? t(descKey) || undefined : undefined,
          missing: t(`what_is_missing_${key}_${level}`) || undefined,
          fix: t(`how_to_improve_${key}_${level}`) || undefined,
          t,
        })
      );
    }
    if (fixItems.length === 0) return '';
    const attrDescKey = TECH_DESC_KEY[group.attrName];
    return renderSgridWithFixes(
      t(group.attrName),
      attrDescKey ? t(attrDescKey) || undefined : undefined,
      fixItems,
      t
    );
  }).join('');
};

const renderBicImprovementsHtml = (data: BicPreviewData, t: Translate) => {
  return BIC_ATTRS.map(({ key, subKeys }) => {
    const attr = data[key];
    const attrTitleKey = BIC_ATTR_TITLE_KEY[key] ?? key;

    const fixItems: string[] = [];
    for (const sk of subKeys) {
      const rule = attr?.scoring?.[sk];
      if (!rule) continue;
      // Only "need fix" items belong in Improvements — skip the ones that pass
      // (score >= 8), matching how the Checks tally counts pass/need-fix.
      if ((rule.score ?? 0) >= 8) continue;
      const descKey = BIC_DESC_KEY[sk];
      fixItems.push(
        renderFixItem({
          title: t(sk),
          desc: descKey ? t(descKey) || undefined : undefined,
          missing: rule.what_is_missing || undefined,
          fix: rule.how_to_improve || undefined,
          t,
        })
      );
    }
    if (fixItems.length === 0) return '';
    const attrDescKey = BIC_DESC_KEY[attrTitleKey];
    return renderSgridWithFixes(
      t(attrTitleKey),
      attrDescKey ? t(attrDescKey) || undefined : undefined,
      fixItems,
      t
    );
  }).join('');
};

const renderProductRuleCases = (entity: RuleEntity, t: Translate) => {
  const currentLabel = t('reportFixCurrent');
  const rewriteLabel = t('reportFixSuggestedRewrite');
  return (entity.examples ?? [])
    .map((ex, i) => {
      const kickLink = ex.link
        ? `<p class="cfd-link">${escapeHtml(ex.link)}</p>`
        : '';
      const currentHtml = ex.current_implementation
        ? `<div class="fxc-line"><span class="fx-miss">${escapeHtml(currentLabel)}:</span> <span class="fxc-txt">${escapeHtml(ex.current_implementation)}</span></div>`
        : '';
      const improvedHtml = ex.improved_version
        ? `<div class="fxc-line"><span class="fx-imp">${escapeHtml(rewriteLabel)}:</span> <span class="fxc-txt">${escapeHtml(ex.improved_version)}</span></div>`
        : '';
      const whyHtml = ex.improvement_rationale
        ? `<div class="fxc-why">${escapeHtml(ex.improvement_rationale)}</div>`
        : '';
      return `<div class="fx-case">
        <div class="fxc-kick">${escapeHtml(t('case'))} ${i + 1}${kickLink ? ` · ${kickLink}` : ''}</div>
        ${whyHtml}
        ${currentHtml}
        ${improvedHtml}
      </div>`;
    })
    .join('');
};

const renderOffImprovementsHtml = (
  services: InsightsService[],
  productRules: ProductRulesPreviewData,
  t: Translate
) => {
  if (services.length === 0) return '';
  const total = services.length;
  return services
    .map((service, i) => {
      const productKey = service.unique_id ?? service.name;
      const perRuleGroups: string[] = [];
      let totalFixes = 0;
      for (const { key, titleKey } of OFFERING_PRODUCT_RULES) {
        const rule = productRules[key];
        const entity: RuleEntity | undefined =
          rule?.products?.[productKey] ?? rule?.products?.[service.name];
        if (!entity) continue;
        if (entity.assessment_score === ScoreEnum.HIGH) continue;
        totalFixes += 1;
        const cases = renderProductRuleCases(entity, t);
        const casesCount = entity.examples?.length ?? 0;
        // Rule description — guard against missing keys leaking the raw slug.
        const descKey = `${titleKey}_desc`;
        const descText = t(descKey);
        const desc = descText && descText !== descKey ? descText : '';
        const verdictHtml = entity.assessment_explanation
          ? `<div class="c-verdict">${escapeHtml(entity.assessment_explanation)}</div>`
          : '';
        perRuleGroups.push(`<div class="sgrid">
          <div class="srail">
            <div class="s-tally">
              <div class="rt-kicker">${escapeHtml(t('cases') || 'Cases')}</div>
              <div class="rt-line"><i></i><span>${casesCount} ${escapeHtml(t('fromYourWebsite') || 'from your website')}</span></div>
            </div>
          </div>
          <div class="scol checks">
            <div class="rh-row plain">
              <div class="rh-stack">
                <div class="rh-name">${escapeHtml(t(titleKey))}</div>
                <div class="rh-desc">${escapeHtml(desc)}</div>
              </div>
            </div>
            <div class="check">
              ${verdictHtml}
              ${cases}
            </div>
          </div>
        </div>`);
      }
      if (perRuleGroups.length === 0) return '';
      const subParts = [
        escapeHtml(service.geographic_focus ?? ''),
        escapeHtml(service.target_audience ?? ''),
      ]
        .filter(Boolean)
        .join(' · ');
      const header = `<div class="sgrid oh">
        <div class="srail"><div class="srail-label faint">${escapeHtml(t('offering') || 'Offering')} ${i + 1} ${escapeHtml(t('of') || 'of')} ${total}</div></div>
        <div class="scol">
          <div class="oh-row">
            <div class="oh-stack">
              <div class="s2-title">${escapeHtml(service.name)}</div>
              <div class="s2-desc">${subParts}</div>
            </div>
            <div class="oh-fixes">${totalFixes} ${escapeHtml(totalFixes === 1 ? t('fixSingular') || 'fix' : t('fixes') || 'fixes')}</div>
          </div>
        </div>
      </div>`;
      return header + perRuleGroups.join('');
    })
    .join('');
};

const applyVisibility = (doc: Document, report: Report, t: Translate) => {
  const { analysis, improvements } = report.settings ?? {};
  const flags: Record<string, boolean> = {
    'analysis-technical': analysis?.tar !== false,
    'analysis-information': analysis?.bic !== false,
    'analysis-offerings': analysis?.off !== false,
    'improvements-technical': improvements?.tar !== false,
    'improvements-information': improvements?.bic !== false,
    'improvements-offerings': improvements?.off !== false,
  };
  for (const [key, on] of Object.entries(flags)) {
    const el = doc.querySelector<HTMLElement>(`[data-section="${key}"]`);
    if (el) {
      el.hidden = !on;
      const table = el.closest<HTMLElement>('table.doc');
      if (table) table.hidden = !on;
    }
    const tocRow = doc.querySelector<HTMLElement>(
      `[data-toc-section="${key}"]`
    );
    if (tocRow) tocRow.hidden = !on;
  }
  const visibleSectionsAll = Array.from(
    doc.querySelectorAll<HTMLElement>('.section[data-section]')
  ).filter(s => !s.hidden);
  visibleSectionsAll.forEach(s => s.classList.remove('page-break'));
  visibleSectionsAll.forEach((s, i) => i > 0 && s.classList.add('page-break'));

  for (const block of ['analysis', 'improvements'] as const) {
    const tocBlock = doc.querySelector<HTMLElement>(
      `[data-toc-block="${block}"]`
    );
    if (tocBlock) {
      const rows = Array.from(
        tocBlock.querySelectorAll<HTMLElement>('[data-toc-section]')
      );
      tocBlock.hidden = rows.every(r => r.hidden);
    }
  }
  const tocPage = doc.querySelector<HTMLElement>('.page.toc');
  if (tocPage) {
    const tocBlocks = Array.from(
      tocPage.querySelectorAll<HTMLElement>('[data-toc-block]')
    );
    tocPage.hidden = tocBlocks.length > 0 && tocBlocks.every(b => b.hidden);
  }
  const visibleTocRows = Array.from(
    doc.querySelectorAll<HTMLElement>('[data-toc-section]')
  ).filter(r => !r.hidden);
  visibleTocRows.forEach((row, i) => {
    const numEl = row.querySelector<HTMLElement>('.tr-num');
    if (numEl) numEl.textContent = String(i + 1).padStart(2, '0');
  });

  const visibleSections = Array.from(
    doc.querySelectorAll<HTMLElement>('.section[data-section]')
  ).filter(s => !s.hidden);
  visibleSections.forEach((section, i) => {
    const skick = section.querySelector<HTMLElement>('.skick');
    if (!skick) return;
    const blockKey =
      section.closest<HTMLElement>('[data-block]')?.dataset.block;
    const label =
      blockKey === 'improvements'
        ? t('improvements') || 'Improvements'
        : t('analysis') || 'Analysis';
    skick.textContent = `${String(i + 1).padStart(2, '0')} · ${label}`;
  });
};

const LOGO_NAME_MAX_CHARS = 65;

const truncateLogoName = (raw: string) =>
  raw.length > LOGO_NAME_MAX_CHARS
    ? `${raw.slice(0, LOGO_NAME_MAX_CHARS - 1).trimEnd()}…`
    : raw;

const buildLogoSvg = (name?: string) => {
  const trimmed = name?.trim();
  if (!trimmed) return `<img src="${ICONS.rivaAILogo}" alt="Riva"/>`;
  const text = truncateLogoName(trimmed);
  const label = escapeHtml(text);
  const width = Math.ceil(33 + text.length * 13.1);
  return `<svg viewBox="0 0 ${width} 20" xmlns="http://www.w3.org/2000/svg">
  <image href="${ICONS.rivaAIIcon}" x="0" y="0" width="20" height="20"/>
  <text x="25.5" y="17.3" font-family="Roboto,sans-serif" font-size="21" font-weight="700" fill="#1a1b21" letter-spacing="0.6">${label}</text>
</svg>`;
};

const appendLogoName = (doc: Document, box: HTMLElement, name?: string) => {
  const text = truncateLogoName(name?.trim() || 'Riva');
  box.style.cssText += ';display:flex;align-items:center;gap:10px';
  const span = doc.createElement('span');
  span.textContent = text;
  span.style.cssText =
    'font:700 16px Roboto,sans-serif;color:#1a1b21;letter-spacing:.5px;white-space:nowrap';
  box.appendChild(span);
};

const applyName = (doc: Document, name?: string) => {
  const box = doc.getElementById('nameBox');
  if (!box) return;
  box.textContent = truncateLogoName(name?.trim() || 'Riva');
};

const applyLogo = (doc: Document, logoUrl?: string, name?: string) => {
  const boxes = Array.from(doc.querySelectorAll<HTMLElement>('.logo-box'));
  for (const box of boxes) {
    box.innerHTML = '';
    box.className = 'logo-box';
    box.hidden = false;
    if (!logoUrl) {
      box.className = 'logo-box bucket-wide';
      box.innerHTML = buildLogoSvg(name);
      const svg = box.querySelector<SVGSVGElement>('svg');
      if (svg) svg.style.maxWidth = 'none';
      continue;
    }
    const img = doc.createElement('img');
    img.alt = '';
    img.style.display = 'block';
    // img.style.width = 'auto';
    img.onload = () => {
      const wide = img.naturalWidth / img.naturalHeight >= 1.4;
      box.className = 'logo-box ' + (wide ? 'bucket-wide' : 'bucket-tall');
      // img.style.height = wide ? '26px' : '40px';
      // img.style.maxWidth = wide ? '150px' : '80px';
    };
    img.src = logoUrl;
    box.appendChild(img);
    appendLogoName(doc, box, name);
  }
};

export const applyReport = (doc: Document, data: PreviewData, t: Translate) => {
  if (!doc.getElementById('logoBox')) return;

  const collectTargets = (id: string): Element[] => {
    const targets: Element[] = [];
    const byId = doc.getElementById(id);
    if (byId) targets.push(byId);
    doc
      .querySelectorAll(`[data-fill="${CSS.escape(id)}"]`)
      .forEach(el => targets.push(el));
    return targets;
  };
  const setText = (id: string, value: string | number | null | undefined) => {
    if (value == null || value === '') return;
    for (const el of collectTargets(id)) el.textContent = String(value);
  };
  const setTextAll = (
    selector: string,
    value: string | number | null | undefined
  ) => {
    if (value == null || value === '') return;
    doc
      .querySelectorAll(selector)
      .forEach(el => (el.textContent = String(value)));
  };
  const setColor = (id: string, status: ScoreDisplayStatus) => {
    for (const el of collectTargets(id)) {
      el.classList.remove('s-green', 's-amber', 's-red');
      el.classList.add(STATUS_CLASS[status]);
    }
  };
  const setScored = (
    id: string,
    score: number | null | undefined,
    max: number | null | undefined = 10
  ) => {
    if (score == null || !Number.isFinite(score)) return;
    setText(id, formatScore(score));
    setColor(id, scoreStatus(score, max));
  };
  // A rule we couldn't evaluate: "-" in place of the number, and none of the
  // pass/partial/fail colours (the template ships a green mock value).
  const setNoScore = (id: string) => {
    for (const el of collectTargets(id)) {
      el.textContent = NO_SCORE;
      el.classList.remove('s-green', 's-amber', 's-red');
    }
  };
  const setPair = (
    prefix: string,
    score: number | null | undefined,
    max: number | null | undefined
  ) => {
    if (score == null || !Number.isFinite(score)) return;
    const status = scoreStatus(score, max);
    setText(`${prefix}-score`, formatScore(score));
    setColor(`${prefix}-score`, status);
    setText(`${prefix}-status`, t(STATUS_LABEL[status]));
    setColor(`${prefix}-status`, status);
  };
  const setTechItem = (
    key: string,
    score: number | null | undefined,
    max: number | null | undefined,
    rawFindings?: Record<string, unknown>
  ) => {
    if (score == null || !Number.isFinite(score)) return;
    const status = getScoreDisplayStatus(score, max);
    const level = techLevel(score, max);
    setText(`atech-item-${key}-score`, formatScore(score));
    setColor(`atech-item-${key}-score`, status);
    setText(`atech-item-${key}-status`, t(STATUS_LABEL[status]));
    setColor(`atech-item-${key}-status`, status);
    setText(`atech-item-${key}-result`, t(`reasoning_${key}_${level}`));

    const findingsEl = doc.getElementById(`atech-findings-${key}`);
    if (!findingsEl) return;
    const titleKey = TECH_TITLE_KEY[key] ?? key;
    const mode = getTechViewMode(titleKey, level);
    const bmap = mode === 'matrix' ? extractBooleanMap(key, rawFindings) : null;
    const header = `<div class="cfd-label">${escapeHtml(t('findings') || 'Findings')}</div>`;
    if (bmap && Object.keys(bmap).length > 0) {
      const rows = Object.entries(bmap)
        .map(
          ([k, v]) =>
            `<div class="cfd-row">– ${escapeHtml(translateFindingKey(k))}: ${v ? '✓' : '✗'}</div>`
        )
        .join('');
      findingsEl.innerHTML = `${header}<div class="cfd-rows">${rows}</div>`;
    } else {
      findingsEl.innerHTML = `${header}<div class="cfd-rows"><div class="cfd-row">${escapeHtml(t(`findings_${key}_${level}`))}</div></div>`;
    }
  };

  applyStaticTranslations(doc, t);
  applyVisibility(doc, data.report, t);
  applyLogo(doc, data.report.logoUrl, data.report.name);
  applyName(doc, data.report.name);

  if (data.domain) {
    setTextAll('.js-domain', data.domain);
    doc
      .querySelectorAll<HTMLAnchorElement>('.js-website-link')
      .forEach(link => {
        link.textContent = data.domain ?? '';
      });
  }
  doc.title = data.domain
    ? `${t('reportDocumentTitle')} — ${data.domain}`
    : t('reportDocumentTitle');
  if (data.reportDate) {
    const formatted = formatDate(new Date(data.reportDate));
    setTextAll('.js-report-date', formatted);
  }

  const BAR_CLASS: Record<ScoreDisplayStatus, string> = {
    pass: 'bg-green',
    partial: 'bg-amber',
    fail: 'bg-red',
  };
  const setOvrBar = (
    id: string,
    score: number | null | undefined,
    max: number | null | undefined = 10
  ) => {
    const el = doc.getElementById(id) as HTMLElement | null;
    if (!el) return;
    if (score == null || !Number.isFinite(score)) return;
    const status = scoreStatus(score, max);
    const maxVal = max && max > 0 ? max : 10;
    const pct = Math.max(0, Math.min(100, (score / maxVal) * 100));
    el.classList.remove('bg-green', 'bg-amber', 'bg-red');
    el.classList.add(BAR_CLASS[status]);
    el.style.width = `${pct}%`;
  };
  // Empty track for a rule with no score, so the bar doesn't keep the
  // template's mock fill.
  const clearOvrBar = (id: string) => {
    const el = doc.getElementById(id) as HTMLElement | null;
    if (!el) return;
    el.classList.remove('bg-green', 'bg-amber', 'bg-red');
    el.style.width = '0';
  };
  const setRuleTally = (
    prefix: string,
    stat: { total: number; pass: number; needFix: number }
  ) => {
    setText(`${prefix}-total`, stat.total);
    setText(`${prefix}-pass`, stat.pass);
    setText(`${prefix}-need-fix`, stat.needFix);
    const passLine = doc.getElementById(`${prefix}-pass-line`);
    if (passLine) {
      passLine.classList.remove('s-green', 'rt-zero');
      passLine.classList.add(stat.pass > 0 ? 's-green' : 'rt-zero');
    }
    const needFixLine = doc.getElementById(`${prefix}-need-fix-line`);
    if (needFixLine) {
      needFixLine.classList.remove('s-red', 'rt-zero');
      needFixLine.classList.add(stat.needFix > 0 ? 's-red' : 'rt-zero');
    }
  };

  const { robots, llms, schema, hasLegacySchemaReadiness } = data.technical;
  // A rule the backend couldn't evaluate is left out of the weighted section
  // score, with the weights renormalized over the remaining rules — counting it
  // as a zero would understate a section we simply couldn't measure. Its checks
  // still count towards the tallies and fix counts, since they're listed in
  // Improvements and do need the reader's attention. A rule that's absent from
  // the response keeps its weight at zero, as before.
  const techRules = TECH_RULE_SPECS.map(spec => {
    const rule = data.technical[spec.name];
    return {
      ...spec,
      rule,
      unscored: isTechRuleUnscored(rule, spec.existsFinding),
    };
  });

  // Keeps the check name, drops the score, the Checks tally and the
  // pass/partial/fail pill, and replaces the template's mock verdict and
  // findings with the "unable to check" copy for every check under the rule.
  const applyUnscoredTechRule = (
    name: TechRuleName,
    items: readonly string[]
  ) => {
    const tallyEl = doc.querySelector<HTMLElement>(
      `#atech-rule-${name} .s-tally`
    );
    if (tallyEl) tallyEl.hidden = true;
    for (const key of items) {
      const statusEl = doc.getElementById(`atech-item-${key}-status`);
      if (statusEl) statusEl.hidden = true;
      setText(`atech-item-${key}-result`, t(`reasoning_${key}_error`));
      const findingsEl = doc.getElementById(`atech-findings-${key}`);
      if (!findingsEl) continue;
      // Without a domain there's no source URL to cite, so the link is omitted
      // rather than rendered as a bare "https://".
      const link = data.domain
        ? `<p class="cfd-link">${escapeHtml(`https://${data.domain}`)}</p>`
        : '';
      findingsEl.innerHTML =
        `<div class="cfd-label">${escapeHtml(t('findings') || 'Findings')}</div>` +
        `<div class="cfd-rows"><div class="cfd-row">${escapeHtml(t(`findings_${key}_error`))}</div></div>` +
        link;
    }
  };

  if (robots || llms || schema) {
    const scoredRules = techRules.filter(r => !r.unscored);
    const techWeight = scoredRules.reduce((sum, r) => sum + r.weight, 0);
    const overall =
      techWeight > 0
        ? scoredRules.reduce(
            (sum, r) => sum + (r.rule?.score ?? 0) * r.weight,
            0
          ) / techWeight
        : 0;
    const overallStatus = scoreStatus(overall);
    for (const p of ['atech', 'itech'] as const) {
      setText(`${p}-sec-score`, formatScore(overall));
      setColor(`${p}-sec-score`, overallStatus);
      setColor(`${p}-sec-rule`, overallStatus);
      for (const r of techRules) {
        if (r.unscored) setNoScore(`${p}-attr-${r.name}`);
        else setScored(`${p}-attr-${r.name}`, r.rule?.score);
      }
    }
    for (const r of techRules) {
      if (r.unscored) {
        clearOvrBar(`atech-attr-${r.name}-bar`);
        setNoScore(`atech-rule-${r.name}-score`);
      } else {
        setOvrBar(`atech-attr-${r.name}-bar`, r.rule?.score);
        setScored(`atech-rule-${r.name}-score`, r.rule?.score);
      }
      if (r.rule && !r.unscored) {
        setRuleTally(
          `atech-rule-${r.name}`,
          calculateTechnicalSectionStats({ [r.statsKey]: r.rule })
        );
      }
    }
    // A check we couldn't run still needs the reader's attention, so it counts
    // towards the section tally and the fix counts — it just has no score and
    // no pass/fail verdict of its own.
    const atechSectionStat = calculateTechnicalSectionStats({
      robotsRuleData: robots,
      llmsRuleData: llms,
      schemaRuleData: schema,
    });
    setText('atech-sec-total', atechSectionStat.total);
    setText('atech-sec-pass', atechSectionStat.pass);
    setText('atech-sec-need-fix', atechSectionStat.needFix);
    for (const r of techRules) {
      if (r.unscored) {
        applyUnscoredTechRule(r.name, r.items);
        continue;
      }
      if (r.name === 'robots') {
        // robots.txt is scored at rule level; the other two score per finding.
        setTechItem(
          'robots-txt',
          robots?.score,
          robots?.max_score,
          robots?.findings as Record<string, unknown> | undefined
        );
        continue;
      }
      for (const key of r.items) {
        const f = (r.rule as TechnicalRuleResponse | undefined)?.findings?.[
          key
        ];
        setTechItem(key, f?.score, f?.max_score, f);
      }
    }
  } else {
    // The whole Technical Accessibility feature has no score to show for
    // this report — either it's unavailable for this user (not enabled, or
    // the backend rejected all three rule requests), or it's an older report
    // that predates the robots/llms/schema endpoints. Either way, mirror the
    // live UI's ScoreDisclosure fallback: no score, no per-rule detail —
    // never leave the template's mock score/checks in the exported PDF.
    for (const p of ['atech', 'itech'] as const) {
      setNoScore(`${p}-sec-score`);
      for (const r of techRules) setNoScore(`${p}-attr-${r.name}`);
    }
    const sectionEl = doc.getElementById('an-tech');
    if (sectionEl) {
      const tallyEl = sectionEl.querySelector<HTMLElement>('.s-head .s-tally');
      if (tallyEl) tallyEl.hidden = true;
      const scoreBoxEl = sectionEl.querySelector<HTMLElement>(
        '.s-head .s2-score-box'
      );
      if (scoreBoxEl) scoreBoxEl.hidden = true;
      const ovrRowsEl =
        sectionEl.querySelector<HTMLElement>('.s-head .ovr-rows');
      if (ovrRowsEl) ovrRowsEl.hidden = true;
      const ovrNoteEl =
        sectionEl.querySelector<HTMLElement>('.s-head .ovr-note');
      if (ovrNoteEl) ovrNoteEl.hidden = true;
      // An older report that already scored "schema readiness" inside the
      // Business Information Completeness rules isn't really missing this
      // analysis — it's just under the old format — so it gets a blank
      // section (no score, no rule detail) but not the "available soon"
      // copy, which would incorrectly imply the analysis doesn't exist yet.
      const descEl = sectionEl.querySelector<HTMLElement>('.s-head .s2-desc');
      if (
        !hasLegacySchemaReadiness &&
        descEl &&
        !sectionEl.querySelector('.tar-unavailable')
      ) {
        const msg = doc.createElement('div');
        msg.className = 'tar-unavailable';
        msg.textContent = t('analysisBeAvailableSoon');
        descEl.insertAdjacentElement('afterend', msg);
      }
    }
    for (const r of techRules) {
      const ruleEl = doc.getElementById(`atech-rule-${r.name}`);
      if (ruleEl) ruleEl.hidden = true;
    }
    const itechBodyEl = doc.getElementById('itech-body');
    if (itechBodyEl) itechBodyEl.innerHTML = '';
  }

  const bic = data.bic;
  if (Object.values(bic).some(v => v?.score != null)) {
    const overall =
      (bic.business_address?.score ?? 0) * 0.15 +
      (bic.business_name?.score ?? 0) * 0.15 +
      (bic.target_audience?.score ?? 0) * 0.35 +
      (bic.geographic_focus?.score ?? 0) * 0.35;
    const overallStatus = scoreStatus(overall);
    for (const p of ['abic', 'ibic'] as const) {
      // BIC section score is rounded to one decimal (e.g. 5.58 → 5.6).
      setText(`${p}-sec-score`, formatScore1(overall));
      setColor(`${p}-sec-score`, overallStatus);
      setColor(`${p}-sec-rule`, overallStatus);
      for (const { key } of BIC_ATTRS) {
        setScored(`${p}-attr-${key}`, bic[key]?.score);
      }
    }
    for (const { key, subKeys } of BIC_ATTRS) {
      const attr = bic[key];
      setText(`abic-attr-${key}-value`, attr?.identified?.primary);
      setText(
        `abic-attr-${key}-confidence`,
        attr?.identified?.confidence_level
      );
      setText(`abic-attr-${key}-evidence`, attr?.identified?.evidence_strength);
      // Analysis Context — the pages AI actually read (page_urls_analyzed).
      // Always overwritten, so an attribute with no pages can't inherit the
      // template's mock list; its label goes with it.
      const linksEl = doc.getElementById(`abic-attr-${key}-links`);
      if (linksEl) {
        const links = renderInsLinks(attr?.pageUrls);
        linksEl.innerHTML = links;
        linksEl.hidden = !links;
        const linksLabel = linksEl.previousElementSibling;
        if (linksLabel?.classList.contains('ins-label')) {
          (linksLabel as HTMLElement).hidden = !links;
        }
      }
      for (const sk of subKeys) {
        const f = attr?.scoring?.[sk];
        setPair(`abic-item-${sk}`, f?.score, f?.max_score);
        setText(`abic-item-${sk}-verdict`, f?.reasoning);
        // Findings come straight from the attribute's API data (e.g. the
        // businessname endpoint for business_name). Only rebuild when data is
        // present so the design-time mock survives before the hook resolves.
        if (f) {
          const findingsEl = doc.getElementById(`abic-item-${sk}-findings`);
          if (findingsEl) findingsEl.innerHTML = renderBicFindings(f.findings);
        }
      }
      // Always overwritten — an empty result must clear the template's mock
      // statements rather than leave them standing in as real data.
      const insightsHtml = renderInsightsHtml(attr?.extraction, t);
      const insightsEl = doc.getElementById(`abic-attr-${key}-insights`);
      if (insightsEl) insightsEl.innerHTML = insightsHtml;
      // With no explicit statements and no implied indicators there's nothing to
      // evidence, so the whole LLM Identification Insights block goes — the
      // confidence/evidence meta alone isn't worth a section.
      const insxEl = doc.getElementById(`abic-attr-${key}-insx`);
      if (insxEl) insxEl.hidden = !insightsHtml;
      // Per-attribute Checks tally — reuse the same passed/rulesCount the app's
      // section total (useRuleStats.completenessStat) sums, so the numbers stay
      // consistent with section 02 and the rest of the report.
      if (attr?.rulesCount != null) {
        const total = attr.rulesCount;
        const pass = attr.passed ?? 0;
        setRuleTally(`abic-rule-${key}`, {
          total,
          pass,
          needFix: Math.max(total - pass, 0),
        });
      }
    }
  }

  const s = data.stats;
  const improvPass = s.technical.pass + s.completeness.pass + s.products.pass;
  const improvTotal =
    s.technical.total + s.completeness.total + s.products.total;
  const improvNeedFix =
    s.technical.needFix + s.completeness.needFix + s.products.needFix;
  setText('improv-pass', improvPass);
  setText('improv-total', improvTotal);
  setText('improv-need-fix', improvNeedFix);
  // Improvements TOC summary sentence — counts interpolated into the localized
  // string (the numbers were previously hard-coded in the translation).
  setText(
    'improv-toc-desc',
    t('reportImprovementsTocDesc', '_global', '', {
      pass: improvPass,
      total: improvTotal,
      needFix: improvNeedFix,
    })
  );
  setText('itech-sec-need-fix', s.technical.needFix);
  setText('itech-sec-total', s.technical.total);
  setText('abic-sec-total', s.completeness.total);
  setText('abic-sec-pass', s.completeness.pass);
  setText('abic-sec-need-fix', s.completeness.needFix);
  setText('ibic-sec-need-fix', s.completeness.needFix);
  setText('ibic-sec-total', s.completeness.total);
  setText('ioff-sec-need-fix', s.products.needFix);
  setText('ioff-sec-total', s.products.total);
  setText('ioff-sec-total-passed', s.products.total);
  setText('ioff-sec-pass', s.products.pass);
  setText('aoff-services-count', data.servicesCount);
  setText('ioff-services-count', data.servicesCount);

  const offeringsBody = doc.getElementById('aoff-body');
  if (offeringsBody && Object.keys(data.offerings.products).length > 0) {
    const html = renderOfferingsHtml(data.offerings, data.domain, t);
    if (html) offeringsBody.innerHTML = html;
  }

  const tocOfferingsEl = doc.getElementById('toc-aoff-list');
  if (tocOfferingsEl && Object.keys(data.offerings.products).length > 0) {
    const html = renderTocOfferings(data.offerings);
    if (html) tocOfferingsEl.innerHTML = html;
  }

  const itechBody = doc.getElementById('itech-body');
  if (
    itechBody &&
    (data.technical.robots || data.technical.llms || data.technical.schema)
  ) {
    const html = renderTechImprovementsHtml(data.technical, t);
    if (html) itechBody.innerHTML = html;
  }

  const ibicBody = doc.getElementById('ibic-body');
  if (ibicBody && Object.values(data.bic).some(v => v?.scoring)) {
    const html = renderBicImprovementsHtml(data.bic, t);
    if (html) ibicBody.innerHTML = html;
  }

  const ioffBody = doc.getElementById('ioff-body');
  if (
    ioffBody &&
    data.offerings.services.length > 0 &&
    (data.productRules.conversational ||
      data.productRules.statistical ||
      data.productRules.freshness)
  ) {
    const html = renderOffImprovementsHtml(
      data.offerings.services,
      data.productRules,
      t
    );
    if (html) ioffBody.innerHTML = html;
  }
};

const pickBic = (h: {
  score?: number;
  scoring?: BicAttrPreviewData['scoring'];
  identified?: BicAttrPreviewData['identified'];
  extraction?: BicAttrPreviewData['extraction'];
  urlsAnalyzed?: string[];
  passed?: number;
  rulesCount?: number;
}): BicAttrPreviewData => ({
  score: h.score,
  scoring: h.scoring,
  identified: h.identified,
  extraction: h.extraction,
  pageUrls: h.urlsAnalyzed,
  passed: h.passed,
  rulesCount: h.rulesCount,
});

export const usePreviewData = (report: Report): PreviewData => {
  const { selectedProject, lastReport } = useCachedSelectedProject();
  const activeReport = useAppSelector(state => state.history.activeReport);
  const {
    ruleData: robotsData,
    status: robotsStatus,
    hasRuleData: hasRobotsRuleData,
  } = useCachedRobotsTxtRule();
  const {
    ruleData: llmsData,
    status: llmsStatus,
    hasRuleData: hasLlmsRuleData,
  } = useCachedLLmsRule();
  const {
    ruleData: schemaData,
    status: schemaStatus,
    hasRuleData: hasSchemaRuleData,
  } = useCachedSchemaRule();
  // A rejected request (e.g. the feature isn't enabled for this user, so the
  // backend returns 500) must not be treated as a rule with no findings —
  // mirrors useTechnicalAccessibilityState's safeRuleData, so the PDF shows
  // the same "available soon" fallback as the live UI instead of a fake score.
  // RTK Query's QueryStatus.rejected === 'rejected' — compared as a literal
  // to avoid importing '@reduxjs/toolkit/query' into this module (a subpath
  // that pulls in an ESM-only transitive dependency Jest can't transform).
  const robots =
    robotsStatus !== 'rejected' && hasRobotsRuleData ? robotsData : undefined;
  const llms =
    llmsStatus !== 'rejected' && hasLlmsRuleData ? llmsData : undefined;
  const schema =
    schemaStatus !== 'rejected' && hasSchemaRuleData ? schemaData : undefined;
  const businessName = useCachedBusinessName();
  const businessAddress = useCachedBusinessAddress();
  const targetAudience = useCachedTargetAudience();
  const geographicFocus = useCachedGeographicFocus();
  const { services } = useCachedBusinessInsights();
  const { productBlocks } = useCachedVisibilityScore();
  const { ruleData: conversational } = useCachedConversationalWritingRule();
  const { ruleData: statistical } = useCachedStatisticalEvidenceRule();
  const { ruleData: freshness } = useCachedContentFreshnessRule();
  const { technicalStat, completenessStat, productsStat } = useRuleStats();
  // Older reports predate the dedicated robots/llms/schema endpoints, but
  // already scored a "schema readiness" criterion inside the Business
  // Information Completeness rules — that data means technical accessibility
  // isn't really unavailable for this report, just scored under the older
  // format, so the "available soon" fallback below must not fire for it.
  const hasLegacySchemaReadiness = [
    businessName.scoring,
    businessAddress.scoring,
    targetAudience.scoring,
    geographicFocus.scoring,
  ].some(scoring => !!scoring?.schema_readiness);

  return {
    report,
    domain: selectedProject?.domain,
    // In history mode the preview must show the date of the report actually
    // being viewed (activeReport), not always the latest one — otherwise the
    // date on the page is out of sync with the rule/insights data below it,
    // which already keys off activeReport.
    reportDate: activeReport?.date ?? lastReport?.date,
    technical: { robots, llms, schema, hasLegacySchemaReadiness },
    bic: {
      business_name: pickBic(businessName),
      business_address: pickBic(businessAddress),
      target_audience: pickBic(targetAudience),
      geographic_focus: pickBic(geographicFocus),
    },
    stats: {
      technical: technicalStat,
      completeness: completenessStat,
      products: productsStat,
    },
    servicesCount: services.length,
    offerings: {
      products: productBlocks,
      services,
    },
    productRules: {
      conversational,
      statistical,
      freshness,
    },
  };
};
